import AddCategoryModal from "@/src/components/action-modals/add-category-modal";
import CategoriesTable from "@/src/components/dashboard/category-table";
import { type IconProps } from "@/src/components/members/stats-card";
import StatsRow from "@/src/components/members/stats-row";
import UserDashboardHeader from "@/src/components/members/user-dashboard-header";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import ToolbarCreateButton from "@/src/components/ui-kit/toolbar-create-button";
import { CategoryTag } from "@/src/models/category.model";
import { useInsertCategoryMutation } from "@/src/services/api/category/category.hooks";
import { useGetEventsQuery } from "@/src/services/api/events/events.hooks";
import { useGetGroups } from "@/src/services/api/groups/groups.hooks";
import { useAllNews } from "@/src/services/api/news/news.hooks";
import { useGetPostsQuery } from "@/src/services/api/posts/posts.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import {
  ArchiveBook,
  Calendar,
  Filter,
  People,
  TableDocument,
} from "iconsax-react-nativejs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, ScrollView, Text, View } from "react-native";

type StatItem = Readonly<{
  count: number;
  text: string;
  icon: React.ReactElement<IconProps>;
}>;

const Dashboard = () => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [filterTrigger, setFilterTrigger] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [filteredTotalCount, setFilteredTotalCount] = useState(0);

  const { mutate: insertCategory, isPending: isInserting } =
    useInsertCategoryMutation();

  // Content counts
  const { data: postsData } = useGetPostsQuery();
  const { data: newsData } = useAllNews();
  const { data: eventsData } = useGetEventsQuery({
    where: {},
    order: [],
    pageSize: 1,
  });
  const { data: groupsData } = useGetGroups();

  const totalPosts = postsData?.pages?.[0]?.getposts?.totalCount ?? 0;
  const totalNews = newsData?.pages?.[0]?.totalCount ?? 0;
  const totalEvents = eventsData?.pages?.[0]?.totalCount ?? 0;
  const totalGroups = groupsData?.pages?.[0]?.getgroups?.totalCount ?? 0;

  const iconColor = themeVars["--color-chalk-default"] as string;

  const stats: ReadonlyArray<StatItem> = [
    {
      count: totalPosts,
      text: "LABEL_TOTAL_POSTS",
      icon: <TableDocument color={iconColor} />,
    },
    {
      count: totalNews,
      text: "LABEL_TOTAL_NEWS",
      icon: <ArchiveBook color={iconColor} />,
    },
    {
      count: totalEvents,
      text: "LABEL_TOTAL_EVENTS",
      icon: <Calendar color={iconColor} />,
    },
    {
      count: totalGroups,
      text: "LABEL_TOTAL_GROUPS",
      icon: <People color={iconColor} />,
    },
  ];

  const tagCountLabel = `${activeFilterCount > 0 ? filteredTotalCount : 0} ${t("LABEL_TAGS")}`;

  const handleAddCategory = (categoryName: string, tag: CategoryTag | null) => {
    insertCategory(
      { categoryName, tags: tag ? [tag] : [] },
      { onSuccess: () => setIsAddModalVisible(false) },
    );
  };

  return (
    <View className="flex-row flex-1 bg-obsidian">
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <View className="flex-1 flex-col rounded-2xl gap-2">
          <UserDashboardHeader bgImageName="office" />
          <StatsRow stats={stats} />

          {/* Categories card */}
          <View className="bg-chalk rounded-2xl pt-6 px-4 xl:px-6 pb-2 gap-4 md:border md:border-ash-3">
            <View className="border-b-2 border-primary self-start px-1 pb-2">
              <Text className="text-primary font-semibold text-sm">
                {t("LABEL_CATEGORIES")}
              </Text>
            </View>

            <View className="flex-row items-stretch gap-3 xl:justify-between">
              <ScSearchInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholderKey="PLACEHOLDER_SEARCH_CATEGORIES"
                containerClassName="rounded-2xl flex-1 xl:max-w-xl"
                onCreatePress={() => setIsAddModalVisible(true)}
              />
              <View className="flex-row gap-3 items-stretch">
                <View className="flex-row items-center justify-center bg-ash rounded-2xl px-4 md:border md:border-ash-3">
                  <Pressable
                    onPress={() => setFilterTrigger(true)}
                    className="flex-row items-center gap-2 xl:pr-3 xl:border-r xl:border-ash-3 hover:bg-ash-3 hover:rounded-lg"
                  >
                    <Filter
                      size={18}
                      color={themeVars["--color-primary-default"] as string}
                    />
                    <Text className="text-slate text-xs hidden xl:flex">
                      {t("LABEL_FILTER")}
                    </Text>
                    {activeFilterCount > 0 && (
                      <View className="bg-primary rounded-full px-2 justify-center items-center">
                        <Text className="text-chalk text-xs">
                          {activeFilterCount}
                        </Text>
                      </View>
                    )}
                  </Pressable>
                  <View className="hidden xl:flex flex-row items-center pl-3">
                    <Text className="text-slate text-xs">{tagCountLabel}</Text>
                  </View>
                </View>

                <ToolbarCreateButton
                  text="BTN_ADD"
                  onPress={() => setIsAddModalVisible(true)}
                  hideOnMobile
                />
              </View>
            </View>

            <CategoriesTable
              searchQuery={searchQuery}
              filterTrigger={filterTrigger}
              onFilterTriggerConsumed={() => setFilterTrigger(false)}
              onFilterCountChange={setActiveFilterCount}
              onTotalCountChange={setFilteredTotalCount}
            />
          </View>
        </View>
      </ScrollView>

      <AddCategoryModal
        visible={isAddModalVisible}
        onAdd={handleAddCategory}
        onCancel={() => setIsAddModalVisible(false)}
        loading={isInserting}
      />
    </View>
  );
};

export default Dashboard;
